import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H1,
  H3,
  H5,
  H6,
} from "@sellar-io/components";
import {
  SupplierRelationshipGetResponseType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsType,
} from "@sellar-io/api-v2/types";
import { getBusinessEntityTypeName, pluralize } from "@sellar-io/common";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  DropdownLegacy,
  Icon,
  ProfilePicture,
  DropdownLegacyElement,
} from "../../../components";
import { AddressFragment } from "../../../components/addressFragment/AddressFragment";
import { BadPayerWarning } from "../../../components/badPayerWarning/BadPayerWarning";
import { RequestedCreditTermsAlert } from "../../../components/connectionPaymentTermsRequest/RequestedCreditTermsAlert";
import { EditDeliveryInstructionsDialog } from "../../../components/deliveryInstructions/EditDeliveryInstructionsDialog";
import { useUpdateConnectionDeliveryInstructions } from "../../../components/deliveryInstructions/useUpdateDeliveryInstructions";
import {
  InlineTab,
  InlineTabList,
  InlineTabGroup,
  InlineTabPanels,
  InlineTabPanel,
} from "../../../components/inlineTab/InlineTab";
import { OpeningOrderFeeLabel } from "../../../components/openingOrderFee/OpeningOrderFeeLabel";
import { PaymentTermsIndicator } from "../../../components/paymentTypeIndicator/PaymentTermsIndicator";
import { ORDER_SUPPLIER, STOREFRONT_LINKS } from "../../../constants/routes";
import OrderFulfillmentIndicator from "../../order/orderFulfillmentIndicator/OrderFulfillmentIndicator";
import { CustomerActivity } from "../activity/CustomerActivity";
import { CustomerContacts } from "../CustomerContacts";
import { CustomerOrderList } from "../CustomerOrderList";
import { BusinessConnectionAnalytics } from "./BusinessConnectionAnalytics";
import { BusinessConnectionDiscountsCard } from "./BusinessConnectionDiscountsCard";
import { BusinessConnectionInvoicingInfoCard } from "./BusinessConnectionInvoicingInfoCard";
import { BusinessConnectionProductsCard } from "./BusinessConnectionProductsCard";
import { BusinessConnectionItemDiscountsCard } from "./cards/BusinessConnectionItemDiscountsCard/BusinessConnectionItemDiscountsCard";
import { ConnectionIntegrationMappingCard } from "./ConnectionIntegrationMappingCard";
import { ConnectionAcceptModal } from "./connectionSelector/ConnectionAcceptModal";
import { ConnectionDeclineModal } from "./connectionSelector/ConnectionDeclineModal";
import { ConnectionUpdateModal } from "./connectionSelector/ConnectionUpdateModal";
import { DeleteConnectionAsSupplierDialog } from "./connectionSelector/DeleteConnectionAsSupplierDialog";
import { useOpeningOrderFeeGetByBuyer } from "../../../hooks/api/v2/openingOrderFee/useOpeningOrderFeeGetByBuyer";
import { Alert } from "../../../components/alert/Alert";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { twMerge } from "tailwind-merge";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { StorefrontPreviewer } from "../../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { BusinessConnectionConvertDiscountsCard } from "./BusinessConnectionConvertDiscountsCard";
import { useStorefrontLinksV2Store } from "~/pages/storefrontLinks/v2/StorefrontLinksV2Store";
import { Badge } from "../../../components/badge/Badge";

const getDeliveryMethodDisplayName = (
  connection: SupplierRelationshipGetResponseType,
): string => {
  const { fulfillmentType } = connection;

  if (fulfillmentType === "sellarDistribution") {
    return "Sellar Distribution";
  }

  if (fulfillmentType === "direct") {
    return (
      connection.DrayDeliveryMethod?.name ??
      connection.Relationship?.CustomerZone?.DrayDeliveryMethod?.name ??
      "Direct"
    );
  }

  if (fulfillmentType === "courier") {
    return (
      connection.CourierDeliveryMethod?.name ??
      connection.Relationship?.CustomerZone?.CourierDeliveryMethod?.name ??
      "Courier"
    );
  }

  if (fulfillmentType === "haulier") {
    return (
      connection.PalletDeliveryMethod?.name ??
      connection.Relationship?.CustomerZone?.PalletDeliveryMethod?.name ??
      "Pallet"
    );
  }

  return fulfillmentType;
};

export function BusinessConnection({
  connection,
}: {
  connection: SupplierRelationshipGetResponseType;
}) {
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );
  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  const { setCustomer: setStorefrontLinksCustomer } =
    useStorefrontLinksV2Store();
  const navigate = useNavigate();
  const [assignTermsActive, setAssignTermsActive] = useState<boolean>(false);

  const [deleteConnectionDialogActive, setDeleteConnectionDialogActive] =
    useState(false);

  const [editDeliveryInstructionsActive, setEditDeliveryInstructionsActive] =
    useState<boolean>(false);
  const [declineConnectionModalActive, setDeclineConnectionModal] =
    useState<boolean>(false);
  const {
    updateConnectionDeliveryInstructions,
    isLoading: updatingConnectionDeliveryInstructions,
  } = useUpdateConnectionDeliveryInstructions(connection.id);

  const unbundlingRelease2aEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );

  const unbundlingRelease2bEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const [updateDialogActive, setUpdateDialogActive] = useState<boolean>(false);
  const retailerBusinessDeliveryInstructions =
    connection.Retailer.Business.deliveryInstructions;
  const connectionDeliveryInstructions = connection.deliveryInstructions;
  const deliveryInstructions =
    retailerBusinessDeliveryInstructions ||
    connectionDeliveryInstructions ||
    "No delivery instructions";

  const showDiscountsAlert =
    (connection.PriceTier?.VolumeDiscountsId && !unbundlingRelease2bEnabled) ||
    (unbundlingRelease2bEnabled &&
      connection.PriceTier?.VolumeDiscountsId &&
      !connection.PriceTier.BasePriceTierId);

  return (
    <>
      <div className={"flex w-screen max-w-full flex-col gap-10 p-6"}>
        <div className={"z-10 flex flex-col gap-4"}>
          <div className="@container flex flex-wrap items-start gap-4">
            {connection.SupplierRetailerConnectionPaymentTermsRequest
              ?.status === "pending" && (
              <RequestedCreditTermsAlert
                requestedPaymentTerms={
                  connection.SupplierRetailerConnectionPaymentTermsRequest
                }
                buyerName={connection.Retailer.Business.name}
                connection={{
                  ...connection,
                  PaymentTermsRequest:
                    connection.SupplierRetailerConnectionPaymentTermsRequest,
                }}
              />
            )}
            <BadPayerWarning
              flaggedAsBadPayer={connection.Retailer.flaggedAsBadPayer}
            />
            <div className="w-full @3xl:w-auto">
              <div className="mb-4 flex items-center">
                <ProfilePicture
                  size={"large"}
                  logoUrl={connection.Retailer.Business.logoUrl}
                  name={connection.Retailer.Business.name}
                />

                <div className="ml-2">
                  <div className="flex items-center">
                    <H1 className="text-lg">
                      {connection.Retailer.Business.name}
                    </H1>
                    <BuyerOpeningOrderFeeLabel
                      buyerId={connection.BuyerId ?? 0}
                    />
                  </div>
                  <H6>
                    {[
                      connection.Retailer.Business.DeliveryAddress.city,
                      connection.Retailer.Business.DeliveryAddress.region,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </H6>
                </div>
              </div>
              <div className={"flex flex-wrap justify-between gap-2"}>
                <div className={"flex gap-4"}>
                  <PaymentTermsIndicator
                    connection={{
                      ...connection,
                      paymentDueCalculateFrom:
                        connection.paymentDueCalculateFrom as SupplierRelationshipPaymentDueCalculateFromType,
                      paymentTerms:
                        connection.paymentTerms as SupplierRelationshipPaymentTermsType,
                    }}
                    invoiceCreationStage={
                      connection.Supplier.invoiceCreationStage
                    }
                  />
                  {unbundlingEnabled ? (
                    <Badge icon={"TRUCK"} className="bg-gray-100 text-gray-700">
                      Default: {getDeliveryMethodDisplayName(connection)}
                    </Badge>
                  ) : (
                    <OrderFulfillmentIndicator
                      fulfillmentType={connection.fulfillmentType}
                    />
                  )}
                </div>
              </div>
            </div>
            {connection.approved ? (
              <div
                className={
                  "mt-4 flex w-full flex-row flex-wrap gap-3 @3xl:mt-0 @3xl:ml-auto @3xl:w-auto"
                }
              >
                <Button
                  className="order-1 @3xl:order-2"
                  onClick={() => {
                    navigate(
                      ORDER_SUPPLIER.href({ connectionId: connection.id }),
                    );
                  }}
                >
                  Place order
                </Button>
                <Button
                  className="order-2 @3xl:order-1"
                  color={"secondary"}
                  onClick={() => {
                    setStorefrontLinksCustomer({
                      connectionId: connection.id,
                      name: connection.Retailer.Business.name,
                      logoUrl: connection.Retailer.Business.logoUrl ?? "",
                      postalCode:
                        connection.Retailer.Business.DeliveryAddress.postalCode,
                      priceTierName: connection.PriceTier?.name ?? "",
                      availabilityTierName:
                        connection.AvailabilityTier?.name ?? "",
                    });
                    return navigate(STOREFRONT_LINKS);
                  }}
                >
                  <Icon icon={"SHARE"} />
                  Share pricing
                </Button>
                {isSupplierStorefrontFeatureFlagEnabled ? (
                  <div className="order-3">
                    <StorefrontPreviewButton customer={connection} />
                  </div>
                ) : null}
                <div className={"order-4 flex items-center gap-2"}>
                  <DropdownLegacy>
                    {!connection.approved && (
                      <DropdownLegacyElement
                        onClick={() => setDeclineConnectionModal(true)}
                      >
                        Decline connection
                      </DropdownLegacyElement>
                    )}
                    {connection.approved && (
                      <>
                        <DropdownLegacyElement
                          onClick={() => setUpdateDialogActive(true)}
                        >
                          Edit terms
                        </DropdownLegacyElement>
                        <DropdownLegacyElement
                          onClick={() => setDeleteConnectionDialogActive(true)}
                          className="text-red-500"
                        >
                          Delete customer
                        </DropdownLegacyElement>
                      </>
                    )}
                  </DropdownLegacy>
                </div>
              </div>
            ) : (
              <div className={"flex items-center gap-2"}>
                <Button
                  className={"col-span-1"}
                  color={"primary"}
                  onClick={() => setAssignTermsActive(true)}
                >
                  <Icon icon={"HANDSHAKE"} />
                  Assign terms
                </Button>
                <DropdownLegacy>
                  <DropdownLegacyElement
                    onClick={() => setDeclineConnectionModal(true)}
                  >
                    Decline connection
                  </DropdownLegacyElement>
                </DropdownLegacy>
              </div>
            )}
          </div>
        </div>

        <BusinessConnectionAnalytics connectionId={connection.id} />

        <InlineTabGroup>
          <div className={"flex flex-wrap justify-between gap-2"}>
            <InlineTabList>
              <InlineTab>Contacts & Info</InlineTab>
              {connection.approved && (
                <>
                  <InlineTab>Terms & Pricing</InlineTab>
                  <InlineTab>Orders</InlineTab>
                  <InlineTab>Activity</InlineTab>
                </>
              )}
            </InlineTabList>
          </div>
          <InlineTabPanels>
            <InlineTabPanel className={"flex flex-col gap-6"}>
              <CustomerContacts
                RetailerId={connection.BuyerId ?? 0}
                buyerName={connection.Retailer.Business.name}
                connectionType={connection.connectionType}
              />
              <BusinessConnectionInvoicingInfoCard connection={connection} />
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <H3>Venue info</H3>
                  {!retailerBusinessDeliveryInstructions?.length && (
                    <Button
                      color={"secondary"}
                      onClick={() => setEditDeliveryInstructionsActive(true)}
                    >
                      <Icon icon={"PENCIL"} />
                      Edit
                    </Button>
                  )}
                </CardHeader>
                <CardContent className={"grid gap-y-4 md:grid-cols-2"}>
                  <div className={"flex flex-col"}>
                    <H5>Delivery address</H5>
                    <AddressFragment
                      address={connection.Retailer.Business.DeliveryAddress}
                    />
                  </div>
                  {connection.connectionType === "customer" && (
                    <>
                      <div>
                        <>
                          <H5>Primary email address</H5>
                          <span className={"select-text"}>
                            {connection.Retailer.Business.email}
                          </span>
                          <H5 className="mt-4">Email marketing consent?</H5>
                          <span className={"select-text"}>
                            {connection.mailingListPermission ? "Yes" : "No"}
                          </span>
                        </>
                      </div>
                      <div>
                        <H5>Phone number</H5>
                        <span>
                          {connection.Retailer.Business.phoneNumber || "N/A"}
                        </span>
                      </div>
                    </>
                  )}
                  <div>
                    <H5>Website</H5>
                    <span>
                      {connection.Retailer.Business.websiteUrl || "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>
                      Delivery instructions (
                      {deliveryInstructions ===
                      retailerBusinessDeliveryInstructions
                        ? "Set by customer"
                        : "Set by you"}
                      )
                    </H5>
                    <span className="italic">{deliveryInstructions}</span>
                  </div>
                  <div>
                    <H5>Instagram</H5>
                    <span>
                      {connection.Retailer.Business.instagram || "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Twitter</H5>
                    <span>{connection.Retailer.Business.twitter || "N/A"}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <H3>Business info</H3>
                </CardHeader>
                <CardContent className={"grid gap-y-4 md:grid-cols-2"}>
                  <div>
                    <H5>Trading name</H5>
                    <span>{connection.Retailer.Business.name}</span>
                  </div>
                  <div>
                    <H5>VAT number</H5>
                    <span>
                      {connection.Retailer.Business.vatNumber ?? "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Company name</H5>
                    <span>
                      {connection.Retailer.Business.registeredCompanyName ??
                        "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Company number</H5>
                    <span>
                      {connection.Retailer.Business.companyNumber ?? "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Business entity type</H5>
                    <span>
                      {getBusinessEntityTypeName(
                        connection.Retailer.Business.entityType ?? undefined,
                      )}
                    </span>
                  </div>
                  <div>
                    <H5>AWRS number</H5>
                    <span>
                      {connection.Retailer.Business.awrsNumber ?? "N/A"}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <ConnectionIntegrationMappingCard connection={connection} />
            </InlineTabPanel>
            <InlineTabPanel className={"flex flex-col gap-6"}>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <H3>{!unbundlingRelease2aEnabled && "Payment "}Terms</H3>
                  <Button
                    color={"secondary"}
                    onClick={() => setUpdateDialogActive(true)}
                  >
                    <Icon icon={"PENCIL"} />
                    Edit terms
                  </Button>
                </CardHeader>
                {!unbundlingRelease2aEnabled && (
                  <CardContent className={"flex flex-col gap-4 pt-4"}>
                    <div className="grid gap-y-4 md:grid-cols-2">
                      <div className="col-span-1 flex flex-col">
                        <H5>Account type</H5>
                        <span>
                          {connection.paymentTerms === "proforma" ||
                          !connection.paymentTerms
                            ? "Proforma"
                            : "Credit"}{" "}
                          account
                        </span>
                      </div>
                      {connection.paymentDueCalculateFrom && (
                        <div className="col-span-1 flex flex-col">
                          <H5>Payment due - calculated from</H5>
                          <span>
                            {connection.paymentDueCalculateFrom ===
                            "endOfFollowingMonth"
                              ? "End of following month"
                              : connection.paymentDueCalculateFrom ===
                                  "endOfMonth"
                                ? "End of current month"
                                : "Issue invoice date"}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="col-span-1 flex flex-col">
                      <H5>Number of days credit</H5>
                      <span>
                        {connection.paymentDue
                          ? `${connection.paymentDue} day(s)`
                          : "N/A"}
                      </span>
                    </div>
                  </CardContent>
                )}
              </Card>
              <Card className="bg-white">
                {!unbundlingRelease2aEnabled && (
                  <CardHeader
                    className={twMerge(
                      "bg-white",
                      "flex items-center justify-between",
                    )}
                  >
                    <H3>Pricing</H3>
                  </CardHeader>
                )}
                <CardContent
                  className={twMerge(
                    "grid gap-y-4 md:grid-cols-2",
                    unbundlingRelease2aEnabled && "-mt-4",
                  )}
                >
                  {unbundlingRelease2aEnabled && (
                    <div className="col-span-1 flex flex-col">
                      <H5>Account type</H5>
                      <span>
                        {connection.paymentTerms === "proforma" ||
                        !connection.paymentTerms
                          ? "Proforma"
                          : "Credit"}{" "}
                        account
                        {connection.paymentDue
                          ? `: ${connection.paymentDue} ${pluralize("day", connection.paymentDue)}`
                          : ""}
                      </span>
                    </div>
                  )}
                  <div>
                    <H5>
                      {unbundlingRelease2bEnabled ? "Price list" : "Price tier"}{" "}
                      {!unbundlingRelease2aEnabled && "/ Customer group"}
                    </H5>
                    <span>{connection.PriceTier?.name ?? "N/A"}</span>
                  </div>
                  <div>
                    <H5>VAT percentage</H5>
                    <span>
                      {connection.vatPercentage ||
                      connection.vatPercentage === 0
                        ? connection.vatPercentage
                        : connection.Supplier.vatPercentage}
                      %
                    </span>
                  </div>
                  {unbundlingRelease2aEnabled && (
                    <div>
                      <H5>Order rules</H5>
                      <span>
                        {connection.OrderRules?.name.split(" - ")[0] ?? "N/A"}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
              {showDiscountsAlert && (
                <Alert
                  type="warning"
                  title="Customer is currently assigned to a price tier that has volume discounts"
                  message="These will stack with any discounts you define here"
                />
              )}
              {unbundlingRelease2bEnabled ? (
                <BusinessConnectionConvertDiscountsCard
                  connectionId={connection.id}
                />
              ) : (
                <>
                  <BusinessConnectionDiscountsCard
                    connectionId={connection.id}
                  />
                  <BusinessConnectionItemDiscountsCard
                    connection={{
                      id: connection.id,
                      buyerName: connection.Retailer.Business.name,
                      retailerId: connection.BuyerId ?? 0,
                    }}
                  />
                </>
              )}
              <BusinessConnectionProductsCard connection={connection} />
            </InlineTabPanel>
            <InlineTabPanel className={"flex w-full flex-col gap-6"}>
              <BuyerOpeningOrderFeeLabel
                fullLabel={true}
                buyerId={connection.BuyerId ?? 0}
              />
              <CustomerOrderList connection={connection} />
            </InlineTabPanel>
            <InlineTabPanel>
              <CustomerActivity connection={connection} />
            </InlineTabPanel>
          </InlineTabPanels>
        </InlineTabGroup>
      </div>
      <DeleteConnectionAsSupplierDialog
        active={deleteConnectionDialogActive}
        onDismiss={() => {
          setDeleteConnectionDialogActive(false);
        }}
        connection={connection}
      />
      <ConnectionDeclineModal
        active={declineConnectionModalActive}
        onDismiss={() => setDeclineConnectionModal(false)}
        connectionId={connection.id}
      />
      <ConnectionAcceptModal
        active={assignTermsActive}
        onDismiss={() => setAssignTermsActive(false)}
        connection={connection}
      />
      <ConnectionUpdateModal
        connection={connection}
        active={updateDialogActive}
        onDismiss={() => setUpdateDialogActive(false)}
        onDelete={() => {
          setUpdateDialogActive(false);
          setTimeout(() => {
            setDeleteConnectionDialogActive(true);
          }, 100);
        }}
      />
      <EditDeliveryInstructionsDialog
        description="Include any useful information to remember when delivering to this customer"
        initialDeliveryInstructions={connection.deliveryInstructions ?? ""}
        active={editDeliveryInstructionsActive}
        onDismiss={() => setEditDeliveryInstructionsActive(false)}
        onSubmit={(data) => {
          updateConnectionDeliveryInstructions({
            data,
            onSuccess: () => {
              setEditDeliveryInstructionsActive(false);
            },
          });
        }}
        loading={updatingConnectionDeliveryInstructions}
      />
    </>
  );
}

const BuyerOpeningOrderFeeLabel = ({
  fullLabel = false,
  buyerId,
}: {
  fullLabel?: boolean;
  buyerId: number;
}) => {
  const openingOrderFee = useOpeningOrderFeeGetByBuyer(buyerId);

  if (!openingOrderFee.data) return;

  const fee = openingOrderFee.data;

  if (!fee.Order) return;

  return (
    <div className="mt-3">
      <OpeningOrderFeeLabel fee={fee} fullLabel={fullLabel} />
    </div>
  );
};

const StorefrontPreviewButton = ({
  customer,
}: {
  customer: SupplierRelationshipGetResponseType;
}) => {
  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    productListQuery,
    tab,
    selectedCustomerZone,
    onChangeTab,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    prefillCustomer,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <Button
          onClick={() => {
            prefillCustomer({
              customer,
            });
            setOpenStorefrontPreviewer(true);
          }}
          color="secondary"
        >
          <Icon icon="STOREFRONT_OUTLINE" />
          <span>Preview</span>
        </Button>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer: selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};
